import { type InfiniteData, useQueryClient } from '@tanstack/react-query'

import type { ClientAddressLookupResult } from '@fv/client-types'

import type { AddressBookListConfig } from './types'
import { addressBookKeys } from './useAddressBookAddresses'

export type AddressBookQueryData = InfiniteData<{
  data: ClientAddressLookupResult[]
  continuationToken?: string
}>

const useAddressBookCacheInvalidator = () => {
  const queryClient = useQueryClient()
  return (ms = 5000) => {
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: addressBookKeys.searchRoot() })
      queryClient.invalidateQueries({ queryKey: addressBookKeys.filterRoot() })
    }, ms)
  }
}

const modifyAddressbookQueryData = (
  data: AddressBookQueryData,
  modifier: (data: ClientAddressLookupResult[]) => ClientAddressLookupResult[],
) => {
  if (!data) return data
  return {
    ...data,
    ...(data.pages && {
      pages: data.pages.map(p => ({
        ...p,
        data: modifier(p.data ?? []),
      })),
    }),
  }
}

export const useAddressBookCacheModifier = (config: AddressBookListConfig) => {
  const queryClient = useQueryClient()
  const invalidate = useAddressBookCacheInvalidator()

  const addToCache = (_mutationResult: ClientAddressLookupResult) => {
    invalidate()
    queryClient.setQueriesData<AddressBookQueryData>(
      {
        queryKey: addressBookKeys.filtered(config),
      },
      data =>
        modifyAddressbookQueryData(data, msgs => [...msgs, _mutationResult]),
    )
  }
  const removeFromCache = (id: string) => {
    invalidate()
    queryClient.setQueriesData<AddressBookQueryData>(
      {
        queryKey: addressBookKeys.all,
      },
      data =>
        modifyAddressbookQueryData(data, msgs =>
          msgs.filter(m => m._id !== id),
        ),
    )
  }
  const replaceInCache = (mutationResult: ClientAddressLookupResult) => {
    invalidate()
    queryClient.setQueriesData<AddressBookQueryData>(
      {
        queryKey: addressBookKeys.all,
      },
      data =>
        modifyAddressbookQueryData(data, msgs =>
          msgs.map(m => (m._id === mutationResult._id ? mutationResult : m)),
        ),
    )
    queryClient.setQueryData<ClientAddressLookupResult>(
      addressBookKeys.byId(mutationResult._id),
      mutationResult,
    )
  }

  return {
    addToCache,
    removeFromCache,
    replaceInCache,
  }
}
